interface Breakpoints {
  small: number
  medium: number
  big: number
  large: number
  nav: number
  xtralarge: number
  hd: number
  hdplus: number
  fullhd: number
}
type BreakpointName = keyof Breakpoints

export const breakpoints: Breakpoints = {
  small: 360,
  medium: 480,
  big: 768,
  large: 992,
  xtralarge: 1200,
  nav: 1260, // hamburger menu breakpoint
  hd: 1280,
  hdplus: 1600,
  fullhd: 1920,
}

/**
 * Ordered largest-to-smallest (fullhd -> small): min-width queries can match simultaneously,
 * and `getCurrentMinWidthBreakpointName` relies on `find` returning the largest matching breakpoint first.
 */
export const mediaQueries = [
  { name: 'fullhd', query: `(min-width: ${breakpoints.fullhd}px)` },
  { name: 'hdplus', query: `(min-width: ${breakpoints.hdplus}px)` },
  { name: 'hd', query: `(min-width: ${breakpoints.hd}px)` },
  { name: 'nav', query: `(min-width: ${breakpoints.nav}px)` },
  { name: 'xtralarge', query: `(min-width: ${breakpoints.xtralarge}px)` },
  { name: 'large', query: `(min-width: ${breakpoints.large}px)` },
  { name: 'big', query: `(min-width: ${breakpoints.big}px)` },
  { name: 'medium', query: `(min-width: ${breakpoints.medium}px)` },
  { name: 'small', query: `(min-width: ${breakpoints.small}px)` },
]

const maxQueriesSensitivity = 0.02

/**
 * Ordered smallest-to-largest (small -> fullhd): max-width queries can match simultaneously,
 * and `getCurrentMaxWidthBreakpointName` relies on `find` returning the smallest matching breakpoint first.
 */
export const mediaQueriesMax = [
  { name: 'small', query: `(max-width: ${breakpoints.small - maxQueriesSensitivity}px)` },
  { name: 'medium', query: `(max-width: ${breakpoints.medium - maxQueriesSensitivity}px)` },
  { name: 'big', query: `(max-width: ${breakpoints.big - maxQueriesSensitivity}px)` },
  { name: 'large', query: `(max-width: ${breakpoints.large - maxQueriesSensitivity}px)` },
  { name: 'xtralarge', query: `(max-width: ${breakpoints.xtralarge - maxQueriesSensitivity}px)` },
  { name: 'nav', query: `(max-width: ${breakpoints.nav - maxQueriesSensitivity}px)` },
  { name: 'hd', query: `(max-width: ${breakpoints.hd - maxQueriesSensitivity}px)` },
  { name: 'hdplus', query: `(max-width: ${breakpoints.hdplus - maxQueriesSensitivity}px)` },
  { name: 'fullhd', query: `(max-width: ${breakpoints.fullhd - maxQueriesSensitivity}px)` },
]

/**
 * Retrieves the numeric value of a specified breakpoint name.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to retrieve.
 * @returns The numeric value of the breakpoint if it exists, otherwise `null`.
 */
export const getBreakpointValue = (breakpointName: BreakpointName): number | null => breakpoints[breakpointName] || null

/**
 * Retrieves the name of the largest breakpoint whose min-width query currently matches
 * (i.e. the breakpoint the viewport has reached or exceeded).
 *
 * @returns {BreakpointName} The name of the current breakpoint. Defaults to 'small' if no media query matches.
 */
export const getCurrentMinWidthBreakpointName = (): BreakpointName =>
  (mediaQueries.find(({ query }) => window.matchMedia(query).matches)?.name ?? 'small') as BreakpointName

/**
 * Retrieves the name of the smallest breakpoint whose max-width query currently matches
 * (i.e. the breakpoint bucket the viewport still fits under).
 *
 * @returns {BreakpointName} The name of the current breakpoint. Defaults to 'fullhd' if no media query matches (viewport wider than the largest breakpoint).
 */
export const getCurrentMaxWidthBreakpointName = (): BreakpointName =>
  (mediaQueriesMax.find(({ query }) => window.matchMedia(query).matches)?.name ?? 'fullhd') as BreakpointName

/**
 * Checks if the specified breakpoint is currently active.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to check.
 * @returns {boolean} - Returns `true` if the breakpoint is active, otherwise `false`.
 */
export const isActiveBreakpoint = (breakpointName: BreakpointName): boolean => {
  const mediaQuery = mediaQueries.find(({ name }) => name === breakpointName)
  if (!mediaQuery) return false

  return window.matchMedia(mediaQuery.query).matches
}

/**
 * Retrieves the MediaQueryList for a given breakpoint name.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to retrieve the media query for.
 * @returns {MediaQueryList | null} - The MediaQueryList object if the breakpoint is found, otherwise null.
 */
export const getBreakpointMediaQuery = (breakpointName: BreakpointName): MediaQueryList | null => {
  const mediaQuery = mediaQueries.find(({ name }) => name === breakpointName)
  return mediaQuery ? window.matchMedia(mediaQuery.query) : null
}

/**
 * Calls `onEnter` while the viewport is at least as wide as the breakpoint (immediately if it already is,
 * then on every crossing upwards) and `onLeave` on every crossing downwards.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to watch.
 * @param {() => void} onEnter - Called when the viewport reaches the breakpoint.
 * @param {() => void} onLeave - Called when the viewport drops below the breakpoint.
 */
export const watchBreakpoint = (breakpointName: BreakpointName, onEnter: () => void, onLeave: () => void): void => {
  const mediaQuery = getBreakpointMediaQuery(breakpointName)
  if (!mediaQuery) return

  if (mediaQuery.matches) {
    onEnter()
  }
  mediaQuery.addEventListener('change', (event) => (event.matches ? onEnter() : onLeave()))
}

/**
 * Checks if the viewport width is below the specified breakpoint (max-width).
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to check.
 * @returns {boolean} - Returns `true` if the viewport is below the breakpoint, otherwise `false`.
 */
export const isUntilBreakpoint = (breakpointName: BreakpointName): boolean => {
  const mediaQuery = mediaQueriesMax.find(({ name }) => name === breakpointName)
  if (!mediaQuery) return false

  return window.matchMedia(mediaQuery.query).matches
}

/**
 * Retrieves the MediaQueryList for a given breakpoint name (max-width).
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to retrieve the media query for.
 * @returns {MediaQueryList | null} - The MediaQueryList object if the breakpoint is found, otherwise null.
 */
export const getBreakpointMediaQueryMax = (breakpointName: BreakpointName): MediaQueryList | null => {
  const mediaQuery = mediaQueriesMax.find(({ name }) => name === breakpointName)
  return mediaQuery ? window.matchMedia(mediaQuery.query) : null
}
